<template>
  <BasePopup
    ref="basePopup"
    :title="view.formTitle"
    class="list-form"
    :compact="true"
    @hide="view.clearState()"
  >
    <form class="list-form__form" @submit.prevent="submitForm">
      <label class="list-form__label" for="list-title">List title</label>
      <!-- eslint-disable vue/no-mutating-props -->
      <InputWithContextMenu
        id="list-title"
        v-model="view.title"
        class="list-form__input"
        type="text"
        placeholder="Choose a title"
      />
      <!-- eslint-enable vue/no-mutating-props -->
      <p v-for="(error, i) in view.errors" :key="i" class="list-form__error">
        {{ error }}
      </p>

      <div class="list-form__controls">
        <AppButton
          class="list-form__btn list-form__btn--cancel"
          :outlined="true"
          type="button"
          @click="basePopup?.hide"
          >Cancel</AppButton
        >
        <AppButton
          class="list-form__btn list-form__btn--submit"
          type="submit"
          :loading="view.isLoading"
          :disabled="view.isConfirmBtnDisabled"
          >{{ view.confirmButtonLabel }}</AppButton
        >
      </div>
    </form>
  </BasePopup>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'

import { CustomListForm } from '@/models/custom.lists'
import { PublicInterface } from '@/models/interfaces'

import BasePopup from '@/components/ui/BasePopup.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import AppButton from '@/app/components/button/AppButton.vue'

const props = defineProps<{
  view: PublicInterface<CustomListForm>
}>()

const basePopup = ref<InstanceType<typeof BasePopup> | null>(null)

async function submitForm(): Promise<void> {
  const success = await props.view.submitForm()

  // Only close the popup if the form was submitted
  // without errors.
  if (success) {
    basePopup.value?.hide()
  }
}

function show(): void {
  basePopup.value?.show()
}

onBeforeUnmount(() => {
  props.view.clearState()
})

defineExpose({ show })
</script>

<style lang="scss" scoped>
.list-form {
  --input-border-color: #{$c-border-2};
  --input-border-color-hover: #{$c-border-5};
}

.night-mode .list-form {
  --input-border-color: #{$c-black-9};
  --input-border-color-hover: #{$c-border-6};
}

:deep(.list-form__form) {
  padding-top: 16px;
}

:deep(.list-form__label) {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
}

:deep(.list-form__input) {
  width: 100%;
  border-radius: 99px;
  padding: 16px 26px 13px;
  border: 1px solid var(--input-border-color);
  background: transparent;
  color: inherit;
  font-size: 16px;

  @include hover {
    border-color: var(--input-border-color-hover);
  }

  &:focus-visible {
    border-color: var(--input-border-color-hover);
  }

  &:has(+ .list-form__error) {
    border-color: $c-red;
  }
}

:deep(.list-form__error) {
  color: $c-red;
  margin-top: 4px;
}

:deep(.list-form__controls) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  width: 100%;
  margin-top: 24px;

  @media (max-width: $w-mobile-s) {
    flex-direction: column-reverse;
  }
}

:deep(.list-form__btn) {
  flex: 1;
}
</style>
